import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import API from '../../../../services/API'
import { SEVERITY } from '../../makeReady/severity'
import { COLOURS } from './chipTypes'

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

const idOf = (ref) => (ref && typeof ref === 'object' ? ref._id : ref) || null

const pluralise = (label) =>
  !label ? 'items' : /s$/i.test(label) ? label : `${label}s`

const mostCommon = (values) => {
  const counts = new Map()
  values.filter(Boolean).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1))
  let best = null
  counts.forEach((n, v) => {
    if (best === null || n > counts.get(best)) best = v
  })
  return best
}

// Right-hand "Applies to" editor. Candidates are vehicles for Vehicle lists
// and assets for everything else; adds/removes are staged on the builder and
// only persisted when the list is saved (server takes the full id set).
const AppliesToPanel = ({
  loadListID,
  type,
  assignments,
  readOnly,
  onAdd,
  onRemove,
}) => {
  const isVehicle = type === 'Vehicle'
  const [vehicles, setVehicles] = useState([])
  const [assets, setAssets] = useState([])
  const [listTitles, setListTitles] = useState({})
  const [untracked, setUntracked] = useState({ vehicles: [], assets: [] })

  useEffect(() => {
    API.get('/vehicleList').then(
      (res) => res.status === 200 && setVehicles(res.data || []),
    )
    API.get('/assets').then(
      (res) => res.status === 200 && setAssets(res.data || []),
    )
    API.get('/settings/makereadyloadlists?types=all').then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setListTitles(
          Object.fromEntries(res.data.map((l) => [String(l._id), l.title])),
        )
      }
    })
    API.get('/makeready/untracked').then((res) => {
      if (res.status === 200 && res.data) {
        setUntracked({
          vehicles: Array.isArray(res.data.vehicles) ? res.data.vehicles : [],
          assets: Array.isArray(res.data.assets) ? res.data.assets : [],
        })
      }
    })
  }, [])

  // Normalised candidate rows of the right kind
  const candidates = useMemo(() => {
    if (isVehicle) {
      return vehicles.map((v) => {
        const currentId = idOf(v.makeReadyLoadList)
        return {
          _id: v._id,
          kind: 'vehicle',
          label: v.callSign || v.regPlate || v._id,
          subLabel: v.vehicleType || '',
          group: v.vehicleType || '',
          currentListId: currentId,
          currentListTitle: currentId
            ? listTitles[String(currentId)] || 'another list'
            : null,
        }
      })
    }
    return assets.map((a) => {
      const currentId = idOf(a.makeReadyLoadList)
      return {
        _id: a._id,
        kind: 'asset',
        label: a.assetID || a._id,
        subLabel: [a.category, a.subCategory].filter(Boolean).join(' › '),
        group: a.subCategory || a.category || '',
        currentListId: currentId,
        currentListTitle: currentId
          ? a.makeReadyLoadList?.title ||
            listTitles[String(currentId)] ||
            'another list'
          : null,
      }
    })
  }, [isVehicle, vehicles, assets, listTitles])

  const byId = useMemo(
    () => Object.fromEntries(candidates.map((c) => [String(c._id), c])),
    [candidates],
  )

  const listedIds = useMemo(
    () =>
      new Set(
        assignments
          .filter((a) => a.pending !== 'remove')
          .map((a) => String(a._id)),
      ),
    [assignments],
  )

  const options = useMemo(
    () =>
      candidates
        .filter((c) => !listedIds.has(String(c._id)))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [candidates, listedIds],
  )

  // "Untracked X": the dominant grouping among what's already assigned
  // (vehicle type, or asset sub-category) drives which untracked items we
  // surface. With nothing assigned yet, fall back to whatever the server
  // suggested this list for.
  const untrackedHint = useMemo(() => {
    const active = assignments.filter((a) => a.pending !== 'remove')
    const dominant = mostCommon(
      active.map((a) => byId[String(a._id)]?.group).filter(Boolean),
    )
    const pool = isVehicle ? untracked.vehicles : untracked.assets
    let rows = []
    let label = ''
    if (dominant) {
      rows = pool.filter((u) =>
        isVehicle
          ? u.vehicleType === dominant
          : (u.subCategory || u.category) === dominant,
      )
      label = dominant
    } else if (loadListID) {
      rows = pool.filter(
        (u) => String(idOf(u.suggestedLoadList)) === String(loadListID),
      )
      label = isVehicle ? 'vehicle' : 'item'
    }
    rows = rows.filter((u) => !listedIds.has(String(u._id)))
    return rows.length ? { label, rows } : null
  }, [assignments, byId, untracked, isVehicle, loadListID, listedIds])

  const addUntracked = (u) => {
    const known = byId[String(u._id)]
    onAdd(
      known || {
        _id: u._id,
        kind: isVehicle ? 'vehicle' : 'asset',
        label: isVehicle ? u.callSign : u.assetID,
        subLabel: isVehicle
          ? u.vehicleType || ''
          : [u.category, u.subCategory].filter(Boolean).join(' › '),
      },
    )
  }

  const visibleCount = assignments.filter((a) => a.pending !== 'remove').length

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: COLOURS.divider,
        borderRadius: '5px',
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="baseline" gap={0.75} mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Applies to
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {visibleCount} {isVehicle ? 'vehicle' : 'item'}
          {visibleCount === 1 ? '' : 's'}
        </Typography>
      </Stack>

      <Stack gap={0.5} mb={1}>
        {assignments.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing uses this list yet. Staff can only open it from Make Ready
            once it is assigned to a {isVehicle ? 'vehicle' : 'bag or device'}.
          </Typography>
        )}
        {assignments.map((a) => {
          const removed = a.pending === 'remove'
          const added = a.pending === 'add'
          const sev = a.level ? SEVERITY[a.level] : null
          return (
            <Stack
              key={a._id}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                px: 0.75,
                py: 0.4,
                borderRadius: '5px',
                border: '1px solid',
                borderColor: added ? COLOURS.pendingBlue : COLOURS.divider,
                borderStyle: added ? 'dashed' : 'solid',
                opacity: removed ? 0.55 : 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: MONO,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: removed ? 'line-through' : 'none',
                  }}
                  title={a.subLabel || undefined}
                >
                  {a.label}
                </Typography>
                {a.subLabel && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                  >
                    {a.subLabel}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  textAlign: 'right',
                  fontWeight: 600,
                  color: removed
                    ? 'text.disabled'
                    : added
                      ? COLOURS.pendingBlue
                      : sev?.hex || 'text.secondary',
                }}
              >
                {removed
                  ? 'Removes on save'
                  : added
                    ? 'New · saves with list'
                    : sev?.label || ''}
              </Typography>
              {!readOnly && (
                <Tooltip title={removed ? 'Keep' : 'Remove'}>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(a._id)}
                    aria-label={removed ? 'Undo remove' : 'Remove'}
                    sx={{ p: 0.25 }}
                  >
                    {removed ? (
                      <UndoIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <CloseIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )
        })}
      </Stack>

      {!readOnly && (
        <Autocomplete
          size="small"
          options={options}
          value={null}
          blurOnSelect
          clearOnBlur
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onChange={(e, v) => v && onAdd(v)}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase()
            if (!q) return opts.slice(0, 50)
            return opts
              .filter(
                (o) =>
                  o.label.toLowerCase().includes(q) ||
                  o.subLabel.toLowerCase().includes(q),
              )
              .slice(0, 50)
          }}
          renderOption={(props, o) => (
            <li {...props} key={o._id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 600 }}
                >
                  {o.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.subLabel}
                  {o.currentListTitle
                    ? `${o.subLabel ? ' · ' : ''}currently uses ${o.currentListTitle}`
                    : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Add a ${isVehicle ? 'vehicle' : 'bag'} by ID…`}
            />
          )}
        />
      )}

      {!readOnly && untrackedHint && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          Untracked {pluralise(untrackedHint.label)}:{' '}
          {untrackedHint.rows.map((u, i) => (
            <React.Fragment key={u._id}>
              {i > 0 && ', '}
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => addUntracked(u)}
                sx={{
                  fontFamily: MONO,
                  fontSize: '0.75rem',
                  verticalAlign: 'baseline',
                }}
              >
                {isVehicle ? u.callSign : u.assetID}
              </Link>
            </React.Fragment>
          ))}
        </Typography>
      )}
    </Paper>
  )
}

export default AppliesToPanel
